/**
 * A child's mastery of /r/ on one floor, estimated from noisy automatic scores (knowledge tracing:
 * docs/research/rewards-and-level-model.md). The estimate is the probability that the child can
 * produce the floor's /r/ correctly. Each judged attempt updates it by Bayes' rule using the
 * classifier's own error rates, so a misjudged attempt moves it only a little, then adds a small
 * learning step after an accepted attempt. It decays a little after long breaks.
 */

export interface Mastery {
  /** Probability that the child has the floor's /r/ (0-1). */
  p: number;
  /** Judged attempts that went into the estimate. */
  attempts: number;
  /** When it was last updated (ms since the epoch), for decay after breaks. */
  updatedAt: number;
}

/**
 * How often the scorer accepts a correct /r/ and rejects an incorrect one, live (docs/speech.md:
 * about 12% false accepts at the thresholds in use; correct /r/ accepted about 72% from ages 9 up,
 * about 55% at 8 and under).
 */
export const CLASSIFIER = {
  older: { sensitivity: 0.72, specificity: 0.88 },
  young: { sensitivity: 0.55, specificity: 0.87 },
} as const;

/** Knowledge tracing's slip (has it, still errs) and guess (has not, still right). */
export const SLIP = 0.15;
export const GUESS = 0.15;
/** Prior for a floor the child has not practised. */
export const PRIOR = 0.3;
/** Learning step after an accepted attempt (share of the remaining gap). */
export const LEARN = 0.08;
/** After this long without practice the estimate starts to decay ... */
const DECAY_AFTER_MS = 3 * 24 * 3600 * 1000;
/** ... towards the prior, by this share per week, at most to half the gap. */
const DECAY_PER_WEEK = 0.1;

export function newMastery(now: number, p = PRIOR): Mastery {
  return { p, attempts: 0, updatedAt: now };
}

/**
 * Updates the estimate with one judged attempt. Bayes' rule over "the child has it" given what
 * the scorer said, with its sensitivity and specificity; then a learning step after an accept.
 */
export function updateMastery(m: Mastery, accepted: boolean, now: number, young = false): Mastery {
  const { sensitivity, specificity } = young ? CLASSIFIER.young : CLASSIFIER.older;
  const p = decayed(m, now).p;
  // A child who has it still slips sometimes, and one who has not yet sometimes gets it right
  // (knowledge tracing's slip and guess); the scorer then accepts a correct /r/ with its
  // sensitivity and an incorrect one with 1 - specificity.
  const correctIfHas = 1 - SLIP;
  const correctIfNot = GUESS;
  const acceptIf = (correct: number) => correct * sensitivity + (1 - correct) * (1 - specificity);
  const likeHas = accepted ? acceptIf(correctIfHas) : 1 - acceptIf(correctIfHas);
  const likeNot = accepted ? acceptIf(correctIfNot) : 1 - acceptIf(correctIfNot);
  const posterior = (p * likeHas) / (p * likeHas + (1 - p) * likeNot);
  const learned = accepted ? posterior + (1 - posterior) * LEARN : posterior;
  // Bounded away from certainty: one mastery estimate never rules out practice or error.
  return { p: clamp(learned, 0.02, 0.98), attempts: m.attempts + 1, updatedAt: now };
}

/** The estimate after a break: it drifts back towards the prior, slowly and only partly. */
export function decayed(m: Mastery, now: number): Mastery {
  const idle = now - m.updatedAt;
  if (idle <= DECAY_AFTER_MS || m.p <= PRIOR) return m;
  const weeks = (idle - DECAY_AFTER_MS) / (7 * 24 * 3600 * 1000);
  const share = Math.min(0.5, weeks * DECAY_PER_WEEK);
  return { ...m, p: m.p - (m.p - PRIOR) * share };
}

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
